import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Sparkles,
  Brain,
  Wrench,
  ScrollText,
  Trash2,
  Save,
  Loader2,
  RotateCcw,
} from "lucide-react";

interface MemoryRow {
  id: string;
  user_id: string;
  memory_key: string;
  memory_value: string;
  category: string | null;
  importance: number;
  updated_at: string;
}

interface SettingsRow {
  system_prompt: string;
  enabled_tools: string[];
}

const ALL_TOOLS: { name: string; label: string; description: string }[] = [
  {
    name: "search_knowledge",
    label: "Wissensdatenbank durchsuchen",
    description: "Erlaubt dem Assistenten, Preise, Services und FAQs nachzuschlagen.",
  },
  {
    name: "list_available_appointments",
    label: "Freie Termine auflisten",
    description: "Schlägt Nutzern verfügbare Slots der nächsten 14 Tage vor.",
  },
  {
    name: "book_appointment",
    label: "Termin buchen",
    description: "Schreibt verbindliche Buchungen direkt in die Datenbank.",
  },
  {
    name: "create_callback_lead",
    label: "Rückruf-Lead anlegen",
    description: "Speichert Kontaktwünsche als Lead.",
  },
  {
    name: "remember_fact",
    label: "Fakten merken",
    description: "Speichert dauerhafte Notizen pro Nutzer (Memory).",
  },
];

export function AssistantAdmin() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SettingsRow | null>(null);
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [memories, setMemories] = useState<MemoryRow[]>([]);

  const load = async () => {
    setLoading(true);
    const [{ data: s }, { data: m }] = await Promise.all([
      supabase
        .from("agent_settings" as any)
        .select("system_prompt, enabled_tools")
        .eq("singleton", true)
        .maybeSingle(),
      supabase
        .from("agent_memory")
        .select("id, user_id, memory_key, memory_value, category, importance, updated_at")
        .order("updated_at", { ascending: false })
        .limit(200),
    ]);

    const data = s as any;
    if (data) {
      const enabled = Array.isArray(data.enabled_tools)
        ? data.enabled_tools
        : ALL_TOOLS.map((t) => t.name);
      setSettings({ system_prompt: data.system_prompt, enabled_tools: enabled });
      setOriginalPrompt(data.system_prompt);
    }
    setMemories((m as any) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    const { error } = await supabase
      .from("agent_settings" as any)
      .update({
        system_prompt: settings.system_prompt,
        enabled_tools: settings.enabled_tools,
        updated_at: new Date().toISOString(),
      })
      .eq("singleton", true);
    setSaving(false);

    if (error) {
      toast({
        title: "Speichern fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setOriginalPrompt(settings.system_prompt);
    toast({ title: "Gespeichert", description: "Verhaltensregeln & Skills aktualisiert." });
  };

  const toggleTool = (name: string) => {
    if (!settings) return;
    const has = settings.enabled_tools.includes(name);
    setSettings({
      ...settings,
      enabled_tools: has
        ? settings.enabled_tools.filter((t) => t !== name)
        : [...settings.enabled_tools, name],
    });
  };

  const deleteMemory = async (id: string) => {
    const { error } = await supabase.from("agent_memory").delete().eq("id", id);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    setMemories((prev) => prev.filter((m) => m.id !== id));
    toast({ title: "Gelöscht" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="border-b bg-slate-50">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Sparkles className="h-5 w-5 text-cyan-600" />
          Klartech KI-Assistent
        </CardTitle>
        <CardDescription>
          Verwalte Verhaltensregeln, aktive Skills und das gespeicherte Memory.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <Tabs defaultValue="rules">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="rules" className="gap-2">
              <ScrollText className="w-4 h-4" /> Verhaltensregeln
            </TabsTrigger>
            <TabsTrigger value="skills" className="gap-2">
              <Wrench className="w-4 h-4" /> Skills
            </TabsTrigger>
            <TabsTrigger value="memory" className="gap-2">
              <Brain className="w-4 h-4" /> Memory ({memories.length})
            </TabsTrigger>
          </TabsList>

          {/* Verhaltensregeln */}
          <TabsContent value="rules" className="space-y-4">
            <p className="text-sm text-slate-600">
              Dieser Text wird als System-Prompt an den Assistenten gesendet. Bei jedem Speichern
              wird er live aktiv – kein Deploy nötig.
            </p>
            <Textarea
              value={settings?.system_prompt ?? ""}
              onChange={(e) =>
                settings && setSettings({ ...settings, system_prompt: e.target.value })
              }
              rows={18}
              className="font-mono text-xs"
            />
            <div className="flex gap-2">
              <Button onClick={saveSettings} disabled={saving} className="gap-2">
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Speichern
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  settings && setSettings({ ...settings, system_prompt: originalPrompt })
                }
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Zurücksetzen
              </Button>
            </div>
          </TabsContent>

          {/* Skills */}
          <TabsContent value="skills" className="space-y-4">
            <p className="text-sm text-slate-600">
              Aktiviere oder deaktiviere einzelne Tools, die der Assistent aufrufen darf.
            </p>
            <div className="space-y-3">
              {ALL_TOOLS.map((tool) => {
                const enabled = settings?.enabled_tools.includes(tool.name) ?? false;
                return (
                  <div
                    key={tool.name}
                    className="flex items-start justify-between gap-4 p-4 border rounded-lg bg-white"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-sm font-mono font-semibold text-cyan-700">
                          {tool.name}
                        </code>
                        {enabled ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            aktiv
                          </Badge>
                        ) : (
                          <Badge variant="outline">deaktiviert</Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium mt-1">{tool.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{tool.description}</p>
                    </div>
                    <Button
                      variant={enabled ? "outline" : "default"}
                      size="sm"
                      onClick={() => toggleTool(tool.name)}
                    >
                      {enabled ? "Aus" : "An"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button onClick={saveSettings} disabled={saving} className="gap-2">
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Speichern
            </Button>
          </TabsContent>

          {/* Memory */}
          <TabsContent value="memory" className="space-y-4">
            <p className="text-sm text-slate-600">
              Alle vom Assistenten gemerkten Fakten – über alle Nutzer hinweg. DSGVO: Du kannst
              jeden Eintrag einzeln löschen.
            </p>
            {memories.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Noch keine Erinnerungen gespeichert.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {memories.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-start justify-between gap-3 p-3 border rounded-lg bg-white"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-xs font-mono font-semibold text-cyan-700">
                          {m.memory_key}
                        </code>
                        {m.category && (
                          <Badge variant="outline" className="text-xs">
                            {m.category}
                          </Badge>
                        )}
                        <span className="text-xs text-slate-400">
                          ★ {m.importance}
                        </span>
                      </div>
                      <p className="text-sm mt-1 break-words">{m.memory_value}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        User: {m.user_id.slice(0, 8)}… · {new Date(m.updated_at).toLocaleString("de-DE")}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMemory(m.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
